<template>
    <Dialog
        :visible="visible"
        @update:visible="$emit('update:visible', $event)"
        header="สร้าง QR Code ถังดับเพลิง"
        :modal="true"
        :closable="true"
        :style="{ width: '800px' }"
        :breakpoints="{ '960px': '75vw', '640px': '90vw' }"
    >
        <div class="qr-generator-content">
            <div class="p-fluid">
                <!-- ช่วงรหัสถัง -->
                <div class="p-field">
                    <label>ช่วงรหัสถัง <span style="color: red">*</span></label>
                    <div class="p-inputgroup">
                        <InputText 
                            v-model="startCode" 
                            placeholder="รหัสเริ่มต้น เช่น D25000001" 
                            :class="isValidStartCode"
                        />
                        <span class="p-inputgroup-addon">ถึง</span>
                        <InputText 
                            v-model="endCode" 
                            placeholder="รหัสสิ้นสุด เช่น D25005000" 
                            :class="isValidEndCode"
                        />
                    </div>
                    <small v-if="submitted && (!startCode || !endCode)" class="p-error">
                        กรุณาระบุช่วงรหัสถัง
                    </small>
                    <small v-if="codeRangeError" class="p-error">
                        {{ codeRangeError }}
                    </small>
                </div>

                <!-- ข้อมูลช่วงที่เลือก -->
                <div class="p-field" v-if="rangeInfo">
                    <label>ข้อมูลช่วงที่เลือก:</label>
                    <div class="range-info">
                        <div class="info-item">
                            <strong>ช่วงรหัส:</strong> {{ startCode }} - {{ endCode }}
                        </div>
                        <div class="info-item">
                            <strong>จำนวนถังทั้งหมด:</strong> {{ rangeInfo.totalCount }} ถัง
                        </div>
                        <div class="info-item">
                            <strong>จำนวนรอบการพิมพ์:</strong> {{ batchCount }} รอบ
                            <small class="p-text-secondary"> ({{ itemsPerBatch }} ถัง/รอบ)</small>
                        </div>
                    </div>
                </div>

                <!-- รายการรอบการพิมพ์ -->
                <div class="p-field" v-if="printBatches.length > 0">
                    <label>รายการรอบการพิมพ์:</label>
                    <div class="batch-list">
                        <div 
                            v-for="(batch, index) in printBatches" 
                            :key="index"
                            class="batch-item"
                            :class="getBatchItemClass(batch)"
                        >
                            <div class="batch-info">
                                <div class="batch-title">
                                    <strong>รอบที่ {{ index + 1 }}</strong>
                                    <Badge 
                                        :value="getBatchStatusText(batch.status)" 
                                        :severity="getBatchStatusSeverity(batch.status)"
                                    />
                                </div>
                                <div class="batch-details">
                                    <span>{{ batch.startCode }} - {{ batch.endCode }}</span>
                                    <span class="batch-count">({{ batch.items.length }} ถัง)</span>
                                </div>
                                <div v-if="batch.status === 'printing'" class="batch-progress">
                                    <ProgressBar :value="batch.progress" :showValue="true" />
                                </div>
                            </div>
                            <div class="batch-actions">
                                <Button 
                                    :label="getBatchButtonLabel(batch.status)"
                                    :icon="getBatchButtonIcon(batch.status)"
                                    :class="getBatchButtonClass(batch.status)"
                                    :loading="batch.status === 'printing'"
                                    :disabled="batch.status === 'completed'"
                                    @click="printBatch(batch, index)"
                                    size="small"
                                />
                            </div>
                        </div>
                    </div>
                </div>

                <!-- สถิติการพิมพ์ -->
                <div class="p-field" v-if="printStats.total > 0">
                    <label>สถิติการพิมพ์:</label>
                    <div class="print-stats">
                        <div class="stat-item">
                            <span class="stat-label">ทั้งหมด:</span>
                            <span class="stat-value">{{ printStats.total }}</span>
                        </div>
                        <div class="stat-item">
                            <span class="stat-label">พิมพ์แล้ว:</span>
                            <span class="stat-value completed">{{ printStats.completed }}</span>
                        </div>
                        <div class="stat-item">
                            <span class="stat-label">รอพิมพ์:</span>
                            <span class="stat-value pending">{{ printStats.pending }}</span>
                        </div>
                        <div class="stat-item">
                            <span class="stat-label">ความคืบหน้า:</span>
                            <span class="stat-value">{{ Math.round((printStats.completed / printStats.total) * 100) }}%</span>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <template #footer>
            <div class="p-d-flex p-jc-between">
                <Button 
                    label="ตรวจสอบช่วง" 
                    icon="pi pi-search" 
                    class="p-button-info"
                    :loading="checkingRange"
                    @click="checkRange" 
                    :disabled="!startCode || !endCode"
                />
                <div>
                    <Button 
                        label="ปิด" 
                        icon="pi pi-times" 
                        class="p-button-text p-mr-2" 
                        @click="closeModal" 
                    />
                    <Button 
                        label="พิมพ์ทั้งหมด" 
                        icon="pi pi-print" 
                        class="p-button-success"
                        :loading="printingAll"
                        :disabled="printBatches.length === 0 || printStats.completed === printStats.total"
                        @click="printAllBatches" 
                    />
                </div>
            </div>
        </template>
    </Dialog>
</template>

<script>
import QRCode from 'qrcode'

export default {
    name: 'QRCodeGeneratorModal',
    props: {
        visible: {
            type: Boolean,
            default: false
        }
    },
    emits: ['update:visible', 'generated'],
    data() {
        return {
            startCode: '',
            endCode: '',
            submitted: false,
            checkingRange: false,
            printingAll: false,
            codeRangeError: '',
            rangeInfo: null,
            printBatches: [],
            itemsPerBatch: 1000, // จำนวนถังต่อรอบ
            currentPrintIndex: 0
        };
    },

    watch: {
        visible(newVal) {
            if (newVal) {
                this.resetForm();
            }
        }
    },

    computed: {
        isValidStartCode() {
            return { 'p-invalid': this.submitted && !this.startCode };
        },
        isValidEndCode() {
            return { 'p-invalid': this.submitted && !this.endCode };
        },
        batchCount() {
            return this.rangeInfo ? Math.ceil(this.rangeInfo.totalCount / this.itemsPerBatch) : 0;
        },
        printStats() {
            const total = this.printBatches.length;
            const completed = this.printBatches.filter(batch => batch.status === 'completed').length;
            const pending = total - completed;
            
            return { total, completed, pending };
        }
    },

    methods: {
        // ตรวจสอบช่วงรหัส
        async checkRange() {
            if (!this.startCode || !this.endCode) {
                this.$toast.add({
                    severity: 'warn',
                    summary: 'ข้อมูลไม่ครบ',
                    detail: 'กรุณาระบุช่วงรหัสถัง',
                    life: 3000
                });
                return;
            }

            // ตรวจสอบ format รหัส
            if (this.startCode.length < 9) {
                this.codeRangeError = 'รหัสถังต้องมี 9 - 10 หลัก เช่น D25000001';
                return;
            }

            if (this.startCode > this.endCode) {
                this.codeRangeError = 'รหัสเริ่มต้นต้องน้อยกว่าหรือเท่ากับรหัสสิ้นสุด';
                return;
            }

            this.codeRangeError = '';
            this.checkingRange = true;

            try {
                const response = await this.axios.post(`${this.$url_service}/initial/extinguisher`, {
                    body: {
                        topic: 'get-extinguishers-for-qr',
                        startCode: this.startCode,
                        endCode: this.endCode
                    }
                });

                if (response.data.success && response.data.data.length > 0) {
                    this.rangeInfo = {
                        totalCount: response.data.data.length,
                        items: response.data.data
                    };
                    
                    this.createPrintBatches();

                    this.$toast.add({
                        severity: 'success',
                        summary: 'ตรวจสอบช่วงสำเร็จ',
                        detail: `พบถังในช่วงนี้ ${this.rangeInfo.totalCount} ถัง`,
                        life: 3000
                    });
                } else {
                    this.codeRangeError = 'ไม่พบถังดับเพลิงในช่วงรหัสที่ระบุ';
                    this.rangeInfo = null;
                    this.printBatches = [];
                }
            } catch (error) {
                console.error('Error checking range:', error);
                this.codeRangeError = 'เกิดข้อผิดพลาดในการตรวจสอบช่วง';
                this.rangeInfo = null;
                this.printBatches = [];
            } finally {
                this.checkingRange = false;
            }
        },

        // สร้างรายการรอบการพิมพ์
        createPrintBatches() {
            if (!this.rangeInfo || !this.rangeInfo.items) return;

            this.printBatches = [];
            const items = this.rangeInfo.items;
            
            for (let i = 0; i < items.length; i += this.itemsPerBatch) {
                const batchItems = items.slice(i, i + this.itemsPerBatch);
                const batch = {
                    startCode: batchItems[0].code,
                    endCode: batchItems[batchItems.length - 1].code,
                    items: batchItems,
                    status: 'pending', // pending, printing, completed
                    progress: 0
                };
                this.printBatches.push(batch);
            }
        },

        // พิมพ์รอบเดียว
        async printBatch(batch, index) {
            if (batch.status === 'printing' || batch.status === 'completed') return;

            batch.status = 'printing';
            batch.progress = 0;

            try {
                await this.generateQRCodes(batch);
                batch.status = 'completed';
                batch.progress = 100;

                this.$toast.add({
                    severity: 'success',
                    summary: 'พิมพ์สำเร็จ',
                    detail: `พิมพ์รอบที่ ${index + 1} สำเร็จ (${batch.items.length} ถัง)`,
                    life: 3000
                });

                // Emit event
                this.$emit('generated', {
                    batch: index + 1,
                    count: batch.items.length,
                    total: this.printBatches.length
                });

            } catch (error) {
                console.error('Error printing batch:', error);
                batch.status = 'pending';
                batch.progress = 0;

                this.$toast.add({
                    severity: 'error',
                    summary: 'เกิดข้อผิดพลาด',
                    detail: `ไม่สามารถพิมพ์รอบที่ ${index + 1} ได้`,
                    life: 3000
                });
            }
        },

        // พิมพ์ทั้งหมด
        async printAllBatches() {
            const pendingBatches = this.printBatches.filter(batch => batch.status === 'pending');
            if (pendingBatches.length === 0) return;

            this.printingAll = true;

            for (let i = 0; i < this.printBatches.length; i++) {
                const batch = this.printBatches[i];
                if (batch.status === 'pending') {
                    await this.printBatch(batch, i);
                    // หน่วงเวลาระหว่างรอบ
                    await new Promise(resolve => setTimeout(resolve, 2000));
                }
            }

            this.printingAll = false;

            this.$toast.add({
                severity: 'success',
                summary: 'พิมพ์ทั้งหมดสำเร็จ',
                detail: `พิมพ์ QR Code จำนวน ${this.rangeInfo.totalCount} ถัง เรียบร้อย`,
                life: 5000
            });
        },

        // สร้าง QR Code
        async generateQRCodes(batch) {
            let text1 = '';
            let text2 = '';

            for (let i = 0; i < batch.items.length; i++) {
                const item = batch.items[i];
                const code = item.code;
                const fullLink = `${window.location.origin}/information/initial/Extinguisher?code=${code}`;

                // สร้าง QR Code
                const qrDataURL = await QRCode.toDataURL(fullLink, { width: 150 });

                text1 += `
<div id="qrCodeContainer" style="width: 3cm; height: 4.5cm; position: relative; border: 2px; border-style: double; text-align: center; box-shadow: inset 0px 0px 0px 2px #000; border-radius: 8px; margin: 3px; padding: 2px;">
    <div style="font-weight: 800; letter-spacing: 1px; text-shadow: 1px 1px 2px rgba(0,0,0,0.3); font-size: 12px; border-style: solid; border-width: 2px; border-color: #000; color: #000; margin-bottom: 2px;"> สปภ. </div>
    <img src="${qrDataURL}" alt="QR Code" style="width: 90%; max-height: 3.5cm; border"/>
    <div class="code-label" style="margin-top: 24px; padding-top: 1px; padding-bottom: 1px; font-size: 14px; border-style: solid; border-width: 2px; border-color: #000; color: #000;">${code}</div>
</div>`;

                // Update progress
                batch.progress = Math.round(((i + 1) / batch.items.length) * 80); // 80% for generation

                // สร้างหน้าใหม่ทุก 36 QR codes (9x4)
                if ((i + 1) % 36 === 0 || i === batch.items.length - 1) {
                    text2 += `
<div style="display: flex; flex-wrap: wrap; justify-content: center; page-break-after: always; max-width: 29.7cm;">
    ${text1}
</div>`;
                    text1 = '';
                }
            }

            // สร้างหน้าพิมพ์
            const printWindow = window.open('', '_blank');
            printWindow.document.open();
            printWindow.document.write(`
<html>
<head>
    <style>
        @page {
            size: A4 landscape;
            margin: 0.5cm;
        }
        body {
            margin: 0;
            display: flex;
            flex-direction: column;
            align-items: center;
        }
        #qrCodeContainer {
            display: inline-block;
            box-sizing: border-box;
        }
        .qr-page {
            display: flex;
            flex-wrap: wrap;
            justify-content: center;
            width: 29.7cm;
            max-width: 29.7cm;
            min-height: 21cm;
        }
    </style>
</head>
<body>
    ${text2}
</body>
</html>`);
            printWindow.document.close();

            // รอให้โหลดเสร็จแล้วพิมพ์
            await new Promise(resolve => {
                setTimeout(() => {
                    batch.progress = 100;
                    printWindow.print();
                    printWindow.close();
                    resolve();
                }, 1000);
            });
        },

        // Helper methods สำหรับ UI
        getBatchItemClass(batch) {
            return {
                'batch-pending': batch.status === 'pending',
                'batch-printing': batch.status === 'printing',
                'batch-completed': batch.status === 'completed'
            };
        },

        getBatchStatusText(status) {
            const statusMap = {
                'pending': 'รอพิมพ์',
                'printing': 'กำลังพิมพ์',
                'completed': 'เสร็จแล้ว'
            };
            return statusMap[status] || status;
        },

        getBatchStatusSeverity(status) {
            const severityMap = {
                'pending': 'warning',
                'printing': 'info',
                'completed': 'success'
            };
            return severityMap[status] || 'secondary';
        },

        getBatchButtonLabel(status) {
            const labelMap = {
                'pending': 'พิมพ์',
                'printing': 'กำลังพิมพ์',
                'completed': 'เสร็จแล้ว'
            };
            return labelMap[status] || 'พิมพ์';
        },

        getBatchButtonIcon(status) {
            const iconMap = {
                'pending': 'pi pi-print',
                'printing': 'pi pi-spin pi-spinner',
                'completed': 'pi pi-check'
            };
            return iconMap[status] || 'pi pi-print';
        },

        getBatchButtonClass(status) {
            const classMap = {
                'pending': 'p-button-outlined',
                'printing': 'p-button-info',
                'completed': 'p-button-success'
            };
            return classMap[status] || 'p-button-outlined';
        },

        // รีเซ็ตฟอร์ม
        resetForm() {
            this.startCode = '';
            this.endCode = '';
            this.submitted = false;
            this.rangeInfo = null;
            this.codeRangeError = '';
            this.printBatches = [];
            this.currentPrintIndex = 0;
        },

        // ปิด modal
        closeModal() {
            this.resetForm();
            this.$emit('update:visible', false);
        }
    }
};
</script>

<style scoped>
.qr-generator-content {
    padding: 0;
}

.range-info {
    background-color: #f8f9fa;
    border: 1px solid #dee2e6;
    border-radius: 4px;
    padding: 1rem;
    margin-top: 0.5rem;
}

.info-item {
    margin-bottom: 0.5rem;
    padding: 0.25rem 0;
}

.info-item:last-child {
    margin-bottom: 0;
}

.batch-list {
    max-height: 400px;
    overflow-y: auto;
    border: 1px solid #dee2e6;
    border-radius: 4px;
}

.batch-item {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 1rem;
    border-bottom: 1px solid #dee2e6;
    transition: background-color 0.2s;
}

.batch-item:last-child {
    border-bottom: none;
}

.batch-item.batch-pending {
    background-color: #fff3cd;
}

.batch-item.batch-printing {
    background-color: #d1ecf1;
}

.batch-item.batch-completed {
    background-color: #d4edda;
}

.batch-info {
    flex: 1;
    margin-right: 1rem;
}

.batch-title {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    margin-bottom: 0.25rem;
}

.batch-details {
    font-size: 0.875rem;
    color: #6c757d;
}

.batch-count {
    font-weight: 500;
    color: #495057;
}

.batch-progress {
    margin-top: 0.5rem;
}

.batch-actions {
    flex-shrink: 0;
}

.print-stats {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
    gap: 1rem;
    background-color: #f8f9fa;
    border: 1px solid #dee2e6;
    border-radius: 4px;
    padding: 1rem;
}

.stat-item {
    display: flex;
    flex-direction: column;
    text-align: center;
}

.stat-label {
    font-size: 0.875rem;
    color: #6c757d;
    margin-bottom: 0.25rem;
}

.stat-value {
    font-size: 1.25rem;
    font-weight: 600;
    color: #495057;
}

.stat-value.completed {
    color: #28a745;
}

.stat-value.pending {
    color: #ffc107;
}

/* Responsive */
@media (max-width: 768px) {
    .batch-item {
        flex-direction: column;
        align-items: stretch;
        gap: 1rem;
    }
    
    .batch-info {
        margin-right: 0;
    }
    
    .print-stats {
        grid-template-columns: repeat(2, 1fr);
    }
}
</style>